export const en = {
    nav: {
        dashboard: 'Dashboard',
        quoteManagement: 'Quote Management',
        settings: 'Settings',
        surveyManagement: 'Survey Management',
        logout: 'Logout',
        superAdminHQ: 'SuperAdmin HQ',
    },
    dashboard: {
        title: 'Alfresko Executive Analytics Dashboard',
        subtitle: 'Real-time insights for business performance',
        totalQuotes: 'Total Quotes',
        thisMonth: 'This Month',
        monthlyVolume: 'Monthly Quote Volume',
        monthlySubtitle: 'Tracking generation trends and growth patterns',
        noData: 'No Data Available',
        noDataSubtitle: 'Insights will populate as quotes are generated',
        analyzing: 'Analyzing performance data...',
        tooltipText: 'This visualization highlights quote creation trends, enabling quick identification of growth opportunities and seasonal patterns.',
    },
    marketing: {
        title: 'Your Marketing Hook',
        subtitle: 'Share this unique link with your customers. It is branded specifically for your dealership.',
        copied: 'Link copied to clipboard!',
    },
    quotes: {
        title: 'Quote Management',
        subtitle: 'Manage and track all customer quotes in one place',
        selected: 'selected',
        total: 'quotes total',
        page: 'Page',
        of: 'of',
        addManualLead: 'Add Manual Lead',
        sortBy: 'Sort By',
        filterStatus: 'Filter Status',
        viewDetails: 'View Details',
        hideDetails: 'Hide Details',
        addNotes: 'Add Notes',
        deleteQuote: 'Delete Quote',
        sort: {
            nameAZ: '📝 From A to Z',
            topPrices: '💶 Top Prices First',
            newest: '⏳ Newest First',
            byStatus: '🎯 By Status',
        },
        status: {
            all: 'All Statuses',
            PENDING: '⏳ Pending',
            CONTACTED: '📞 Contacted',
            LEFT_MESSAGE: '💬 Left Message',
            SURVEY_BOOKED: '📅 Survey Booked',
            SURVEY_COMPLETED: '✏️ Survey Completed',
            REVISED_ESTIMATE_SENT: '📤 Revised Estimate Sent',
            SALE_AGREED: '🤝 Sale Agreed',
            INVOICED: '🧾 Invoiced',
            PAYMENT_RECEIVED: '💰 Payment Received',
            ORDERED: '📦 Ordered',
            INSTALLED: '🔧 Installed',
            COMPLETE: '✅ Complete',
            APPROVED: '✅ Approved',
            REJECTED: '❌ Rejected',
        },
        details: {
            customerDetails: 'Customer Details',
            configDetails: 'Configuration Details',
            quoteDetails: 'Quote Details',
            customerInfo: 'Customer Information',
            postcode: 'Postcode',
            size: 'Size',
            canopy: 'Canopy',
            roof: 'Roof',
            color: 'Color',
            walls: 'Walls',
            accessories: 'Accessories',
            budget: 'Budget',
            groundCondition: 'Ground Condition',
            customerNotes: 'Customer notes',
            na: 'N/A',
            none: 'None',
        },
        deleteDialog: {
            title: 'Delete Quote',
            body: 'Do you wish to delete this quote? This action cannot be undone.',
            cancel: 'Cancel',
            confirm: 'Delete',
        },
        statusDialog: {
            title: 'Change Status',
            body: 'Do you want to change the status to',
            body2: 'This action will update the quote status.',
            cancel: 'Cancel',
            confirm: 'Confirm',
        },
        manualDialog: {
            title: 'Add Existing Lead',
            customerName: 'Customer Name',
            email: 'Email',
            phone: 'Phone',
            address: 'Address',
            addressPlaceholder: 'Street, City, Postcode',
            productType: 'Product Type',
            estimatedValue: 'Estimated Value',
            notes: 'Specification Notes',
            notesPlaceholder: 'e.g. 3000mm x 2500mm, Anthracite Grey. Includes 2x Heaters and LED Lights.',
            notesHelper: 'Enter dimensions, colors, and accessories here.',
            cancel: 'Cancel',
            save: 'Save Lead',
            saving: 'Saving...',
            products: {
                wallMounted: 'Canopy - Wall Mounted',
                freeStanding: 'Canopy - Free Standing',
                bespoke: 'Other / Bespoke',
            },
        },
        success: {
            deleted: 'Quote deleted successfully',
            statusUpdated: 'Status updated successfully!',
            leadAdded: 'Lead added successfully!',
        },
        survey: {
            noSurvey: 'No Site Survey Submitted',
            sectionTitle: 'Completed Site Survey Details',
            submittedAt: 'Submitted on',
        },
    },
    settings: {
        title: 'Settings',
        subtitle: 'Manage your account',
        nav: {
            users: 'User Management',
            products: 'Product Catalog',
            profile: 'Company Profile',
            security: 'Security',
        },
        users: {
            title: 'Team Members',
            inviteUser: 'Invite User',
            tableUser: 'User',
            tableEmail: 'Email',
            tableRole: 'Role',
            tableActions: 'Actions',
            changeRole: 'Change Role',
            removeUser: 'Remove User',
            roleAdmin: 'Admin',
            roleMember: 'Member',
        },
        products: {
            title: 'Product Catalog',
            tabs: {
                baseConfigs: 'Base Configs',
                walls: 'Walls',
                accessories: 'Accessories',
            },
            tableName: 'Name',
            tableAlias: 'Alias/Components',
            tablePrice: 'Price',
            tableActions: 'Actions',
            updatePrice: 'Update Price',
        },
        profile: {
            title: 'Company Profile',
            identity: 'Identity',
            legalName: 'Legal Name',
            brandName: 'Brand Display Name',
            contact: 'Contact',
            phone: 'Phone',
            website: 'Website',
            address: 'Address',
            financial: 'Financial',
            vatId: 'VAT ID',
            currency: 'Currency',
            saveChanges: 'Save Changes',
            logo: 'Logo',
            uploadLogo: 'Upload Logo',
        },
        priceEditor: {
            title: 'Update Price',
            body: 'Enter new price for',
            front: 'Front',
            sides: 'Sides',
            cancel: 'Cancel',
            save: 'Save',
        },
        invite: {
            title: 'Invite User',
            name: 'Name',
            email: 'Email',
            password: 'Password',
            tooWeak: 'Too weak',
            cancel: 'Cancel',
            invite: 'Invite',
            inviting: 'Sending...',
        },
    },
    login: {
        title: 'Administrative Access',
        subtitle: 'Secure authentication required',
        email: 'Email Address',
        password: 'Password',
        submit: 'Access Portal',
        forgotPassword: 'Forgot Password?',
        poweredBy: 'Powered by Alfresko AV',
    },
    security: {
        title: 'Security Settings',
        subtitle: 'Update your password to keep your account secure.',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm New Password',
        helperText: 'Minimum 6 characters',
        updateButton: 'Update Password',
        errorMatch: 'New passwords do not match.',
        errorLength: 'Password must be at least 6 characters.',
        success: 'Password updated successfully! Please log out and log back in.',
    },
};